import type {
  MaintenanceScheduleRangeType,
  MaintenanceSearchOptionGroupType,
  MaintenanceSearchOptionsItemType,
  MaintenanceType,
} from '@/app/components/sections/service/maintenance-list/types';
import {
  getParsedFormat,
  getScheduleSelectOptionValue,
} from '@/app/components/sections/service/maintenance-list/utils';
import {
  CATEGORY_CUSTOM_LABEL,
  CATEGORY_ORDER,
  CATEGORY_TO_NAME,
  OLD_CV_MODEL_QUERY,
} from '@/app/contents/service/maintenance-list/cv';
import { compareLocaleString } from '@/app/utils/sorts';

export const getCategoryOptionValueAndLabel = (item: MaintenanceType) =>
  item.series_marketing_model_name;

export const getSortedCategoryOptions = (
  options: MaintenanceSearchOptionGroupType[]
) =>
  options
    .sort((a, b) => CATEGORY_ORDER[a.key] - CATEGORY_ORDER[b.key])
    .map((item) => ({
      ...item,
      label: CATEGORY_CUSTOM_LABEL[item?.key] ?? item?.label,
    }));

export const getModelOptionLabel = (item: MaintenanceType) => item.vehicle_name;

export const getModelOptionValue = (item: MaintenanceType) => {
  return getParsedFormat(item?.vehicle_name);
};

export const getYearOptionLabel = (item: MaintenanceType) =>
  item?.marketing_model_year_begin_and_end;

export const getYearOptionValue = (item: MaintenanceType) =>
  `${item?.model_year_begin}`;

const isEuro5 = (item: MaintenanceType) =>
  !!item.european_emission_standard?.includes('5');

// Newest year first, one option per year value.
const getSortedYearOptions = (options: MaintenanceSearchOptionsItemType[]) => {
  const uniqueYears = new Map<string, MaintenanceSearchOptionsItemType>();
  options.forEach((item) => {
    const value = getYearOptionValue(item);
    if (!uniqueYears.has(value)) uniqueYears.set(value, item);
  });

  return Array.from(uniqueYears.values()).sort(
    (a, b) => b.model_year_begin - a.model_year_begin
  );
};

// Natural order so text + numbers sort as read, e.g. "FRR 90" before "FRR 210".
const compareNatural = (a?: string, b?: string) =>
  compareLocaleString(a, b, undefined, { numeric: true, sensitivity: 'base' });

// Leading model code of the grade, so variants stay together (e.g. "FVZ-S" → "FVZ").
const getBaseGrade = (item: MaintenanceType) =>
  item.class_one_marketing_grade?.match(/^[a-z]+/i)?.[0] ??
  item.class_one_marketing_grade;

// Engine size from the vehicle name, e.g. "FRR 210" → 210. Needs 2+ digits so
// gear counts ("NMR เกียร์ 5 สปีด") don't count; 0 when the name has none.
const getEngineSize = (item: MaintenanceType) =>
  parseInt(item.vehicle_name.match(/\d{2,}/)?.[0] ?? '0', 10);

// Within a grade: EURO 5 first, newest year first, bigger engine first
// (e.g. "FRR 210" before "FRR 190"), then longer name first
// (e.g. "FTR Smoother (EURO 5)" before "FTR (EURO 5)").
// Expects children sorted newest first (see getSortedYearOptions).
const compareModelGroups = (
  a: MaintenanceSearchOptionGroupType,
  b: MaintenanceSearchOptionGroupType
) => {
  const groupItemA = a.children[0];
  const groupItemB = b.children[0];

  const gradeDiff = compareNatural(
    getBaseGrade(groupItemA),
    getBaseGrade(groupItemB)
  );
  if (gradeDiff !== 0) return gradeDiff;

  const aIsEuro5 = a.children.some(isEuro5);
  const bIsEuro5 = b.children.some(isEuro5);
  if (aIsEuro5 !== bIsEuro5) return aIsEuro5 ? -1 : 1;

  const yearDiff = groupItemB.model_year_begin - groupItemA.model_year_begin;
  if (yearDiff !== 0) return yearDiff;

  const engineDiff = getEngineSize(groupItemB) - getEngineSize(groupItemA);
  if (engineDiff !== 0) return engineDiff;

  const lengthDiff =
    groupItemB.vehicle_name.length - groupItemA.vehicle_name.length;
  if (lengthDiff !== 0) return lengthDiff;

  return compareNatural(groupItemA.vehicle_name, groupItemB.vehicle_name);
};

/**
 * หลักการเรียงลำดับ "รุ่นรถ" สำหรับ CV Maintenance
 *
 * ระบบจะเปรียบเทียบรุ่นรถทีละคู่ตามกฎด้านล่างตามลำดับ
 * โดยเริ่มจาก Rule 1 และหยุดทันทีเมื่อสามารถตัดสินลำดับได้
 * หากค่าของ Rule ปัจจุบันเท่ากัน จึงไปเปรียบเทียบ Rule ถัดไป
 *
 * ============================================================================
 * SORTING PRIORITY
 * ============================================================================
 *
 * 1. Grade / Model Group
 * 2. EURO 5 Priority
 * 3. Model Year
 * 4. Engine Size
 * 5. Model Name Length
 * 6. Natural Sort
 *
 * ============================================================================
 * RULE 1: Grade / Model Group
 * ============================================================================
 *
 * จัดกลุ่มตามตัวอักษรภาษาอังกฤษด้านหน้าของ `classOneMarketingGrade`
 * และเรียงกลุ่มจาก A → Z
 *
 * Special case:
 * - `FRR-S` ให้นับเป็นกลุ่ม `FRR`
 * - ตัด suffix `-S` ออกเพื่อให้รุ่นย่อยอยู่ในกลุ่มเดียวกับรุ่นหลัก
 *
 * Example:
 * FRR → FTR → FVZ → FXZ → NMR → NPR → NQR
 *
 * ============================================================================
 * RULE 2: EURO 5 Priority
 * ============================================================================
 *
 * ภายใน Grade เดียวกัน รุ่นที่เป็น EURO 5 ต้องอยู่ก่อนรุ่นที่ไม่ใช่ EURO 5
 * Example:
 * NPR (EURO 5)
 * NPR 150
 *
 * ============================================================================
 * RULE 3: Newer Model Year First
 * ============================================================================
 *
 * เรียงตามปีเริ่มต้นล่าสุดของรุ่น (`modelYearBegin`)
 * โดยปีใหม่กว่าอยู่ก่อนปีเก่า
 *
 * Example:
 * FXZ 360      // ปีใหม่กว่า
 * FXZ THE ROCK
 * FXZ 345      // ปีเก่ากว่า
 *
 * หมายเหตุ:
 * Rule นี้ต้องมาก่อน Engine Size เนื่องจากบางรุ่นไม่มีตัวเลขในชื่อ
 * เช่น `FXZ THE ROCK`
 *
 * หากนำ Engine Size มาเปรียบเทียบก่อน รุ่นที่ไม่มีตัวเลขจะถูกตีความเป็น 0
 * และอาจตกไปอยู่ท้ายกลุ่ม ซึ่งไม่ตรงกับลำดับที่ต้องการ
 *
 * ============================================================================
 * RULE 4: Larger Engine Size First
 * ============================================================================
 *
 * ดึงตัวเลขตั้งแต่ 2 หลักขึ้นไปจากชื่อรุ่นเพื่อใช้เป็น Engine Size
 * และเรียงจากค่ามาก → น้อย
 *
 * Examples:
 *
 * FRR 210 → FRR 190
 * FVZ 300 → FVZ 240
 *
 * Rules:
 * - ตัวเลขตั้งแต่ 2 หลักขึ้นไปถือเป็น Engine Size
 * - ตัวเลขหลักเดียว เช่น "เกียร์ 5 สปีด" หรือ "เกียร์ 6 สปีด"
 *   ไม่ถือเป็น Engine Size
 * - หากชื่อรุ่นไม่มีตัวเลขที่เข้าเงื่อนไข ให้ Engine Size = 0
 *
 * Example:
 *
 * NPR (EURO 5) // Engine Size = 0
 *
 * ============================================================================
 * RULE 5: Longer Model Name First
 * ============================================================================
 *
 * หากค่าจาก Rule 1-4 เท่ากัน ให้ชื่อรุ่นที่ยาวกว่าอยู่ก่อน
 *
 * ใช้สำหรับจัดลำดับรุ่นที่มี suffix หรือคำต่อท้ายพิเศษ
 * เช่น `Smoother`
 *
 * Examples:
 *
 * FTR Smoother (EURO 5)
 * FTR (EURO 5)
 *
 * NPR 150 Smoother
 * NPR 150
 *
 * ============================================================================
 * RULE 6: Natural Sort
 * ============================================================================
 *
 * หากทุก Rule ด้านบนเท่ากัน ให้เรียงชื่อแบบ Natural Sort
 * เพื่อให้ข้อความที่มีตัวเลขเรียงตามค่าตัวเลขอย่างเป็นธรรมชาติ
 * แทนการเปรียบเทียบแบบ string ธรรมดา
 *
 * ============================================================================
 * IMPORTANT NOTES
 * ============================================================================
 * ข้อมูลจาก CMS ถือเป็น Source of Truth สำหรับการเรียงลำดับ
 * ดังนั้นลำดับที่แสดงจริงจะขึ้นอยู่กับข้อมูล เช่น:
 *
 * - `classOneMarketingGrade`
 * - `modelYearBegin`
 * - ชื่อรุ่น
 * - สถานะ EURO 5
 *
 * หาก Grade หรือ Model Year ใน CMS แตกต่างจากค่าที่คาดไว้
 * ลำดับที่ได้จากระบบก็จะเปลี่ยนไปตามข้อมูลใน CMS เช่นกัน
 *
 * @example
 * // Sorting priority: Grade -> EURO 5 -> Model Year -> Engine Size -> Model Name Length -> Natural Sort
 */
export const getSortedModelOptions = (
  options: MaintenanceSearchOptionsItemType[]
): MaintenanceSearchOptionGroupType[] => {
  const groupItems = options.reduce((reducer, obj) => {
    const key = getModelOptionValue(obj);
    const existing = reducer.get(key);
    return reducer.set(
      key,
      existing
        ? { ...existing, children: [...existing.children, obj] }
        : { key, label: getModelOptionLabel(obj), children: [obj] }
    );
  }, new Map<string, MaintenanceSearchOptionGroupType>());

  return Array.from(groupItems.values())
    .map((group) => ({
      ...group,
      children: getSortedYearOptions(group.children),
    }))
    .sort(compareModelGroups);
};

export const extractParamsFromUrl = (url: string) => {
  const splitStrings = url.split('_');
  // Correct URL should have more than 2 split strings
  if (splitStrings.length < 2) return;

  const startYear = splitStrings[2];
  return {
    model_year_begin: parseInt(startYear),
  };
};

export const getPageUrlFormatFromOptionValues = (values: string[]) =>
  `${CATEGORY_TO_NAME[values[0]]}_${values[1]}_${values[2]}`;

export const getPageUrlFormat = (item: MaintenanceType) =>
  `${
    CATEGORY_TO_NAME[getCategoryOptionValueAndLabel(item)]
  }_${getModelOptionValue(item)}_${getYearOptionValue(item)}`;

// type=truck-tractor&model=gxz-the-rock&year=2022-present
export const getFallbackUrl = (data: {
  type: string;
  model: string;
  year: string;
}) => {
  const startYear = data.year.split('-')[0];
  if (OLD_CV_MODEL_QUERY[data.model]) {
    return `${data.type}_${OLD_CV_MODEL_QUERY[data.model]}_${startYear}`;
  }
  return `${data.type}_${data.model.replace(/-/g, '')}_${startYear}`;
};

export const createTrackingValue = (
  item: MaintenanceType,
  schedule?: MaintenanceScheduleRangeType
) =>
  `${getCategoryOptionValueAndLabel(item)} ${getModelOptionLabel(item)} ${getYearOptionLabel(item)}${schedule ? ` ${getScheduleSelectOptionValue(schedule)}` : ''}`;
